import React from 'react';
import type { RotationOverview } from '../../../types/biweeklyRotation';
import { getExecutionModeStyle, formatTimeUntil, formatPct } from '../../../types/biweeklyRotation';

interface OverviewTabProps {
  overview: RotationOverview | null;
  loading: boolean;
  onRefresh: () => void;
  onNavigateToPreview: () => void;
  onNavigateToHistory: () => void;
  onNavigateToSettings: () => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  overview,
  loading,
  onRefresh,
  onNavigateToPreview,
  onNavigateToHistory,
  onNavigateToSettings,
}) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-neutral-400">Loading overview...</div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="rounded-2xl border border-dashed border-white/10 bg-neutral-800/40 py-12 text-center">
        <p className="text-neutral-400">Failed to load rotation overview</p>
        <button
          onClick={onRefresh}
          className="mt-4 btn btn-outline px-4 py-2"
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  const modeStyle = getExecutionModeStyle(overview.status.executionMode);

  return (
    <div className="space-y-6">
      {/* Status Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rotation Status Card */}
        <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Rotation Status</h3>
            <span
              className={`badge text-sm px-3 py-1 ${modeStyle.bgColor} ${modeStyle.textColor} border ${modeStyle.borderColor}`}
            >
              {overview.status.executionModeLabel}
            </span>
          </div>

          <div className="space-y-4">
            {/* Active Status */}
            <div className="flex items-center gap-3">
              <div
                className={`w-3 h-3 rounded-full ${
                  overview.status.isActive ? 'bg-emerald-400 animate-pulse' : 'bg-neutral-500'
                }`}
              />
              <span className="text-neutral-300">
                {overview.status.isActive ? 'Active' : 'Inactive'} - Every{' '}
                {overview.schedule.cadenceDays} days
              </span>
            </div>

            {/* Next Rotation */}
            <div className="rounded-xl bg-neutral-900/50 p-4">
              <p className="text-sm text-neutral-400 mb-1">Next Rotation</p>
              <p className="text-xl font-semibold text-white">
                {overview.status.nextRotationFormatted}
              </p>
              <p className="text-sm text-cyan-400 mt-1">
                in {formatTimeUntil(overview.status.timeUntilNext)}
              </p>
            </div>
          </div>
        </div>

        {/* Last Rotation Card */}
        <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Last Rotation</h3>

          {overview.lastRotation ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Date</span>
                <span className="text-white font-medium">
                  {overview.lastRotation.formattedDate}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-neutral-900/50 p-3">
                  <p className="text-xs text-neutral-400">Exits</p>
                  <p className="text-lg font-semibold text-red-300">
                    {overview.lastRotation.summary.totalExits}
                  </p>
                </div>
                <div className="rounded-xl bg-neutral-900/50 p-3">
                  <p className="text-xs text-neutral-400">Adds</p>
                  <p className="text-lg font-semibold text-emerald-300">
                    {overview.lastRotation.summary.totalAdds}
                  </p>
                </div>
                <div className="rounded-xl bg-neutral-900/50 p-3">
                  <p className="text-xs text-neutral-400">Turnover</p>
                  <p className="text-lg font-semibold text-white">
                    {formatPct(overview.lastRotation.summary.turnoverPct)}
                  </p>
                </div>
                <div className="rounded-xl bg-neutral-900/50 p-3">
                  <p className="text-xs text-neutral-400">Cost</p>
                  <p className="text-lg font-semibold text-white">
                    {overview.lastRotation.summary.costBps} bps
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="badge bg-emerald-500/20 text-emerald-100 border border-emerald-500/30 text-xs">
                  {overview.lastRotation.status}
                </span>
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-neutral-900/50 p-6 text-center">
              <p className="text-neutral-500">No rotations executed yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Statistics Section */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">
          Statistics (Last 30 Days)
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-xl bg-neutral-900/50 p-4 text-center">
            <p className="text-sm text-neutral-400 mb-1">Total Rotations</p>
            <p className="text-2xl font-bold text-white">
              {overview.statistics.totalRotationsLast30Days}
            </p>
          </div>
          <div className="rounded-xl bg-neutral-900/50 p-4 text-center">
            <p className="text-sm text-neutral-400 mb-1">Avg Turnover</p>
            <p className="text-2xl font-bold text-white">
              {formatPct(overview.statistics.avgTurnoverPct)}
            </p>
          </div>
          <div className="rounded-xl bg-neutral-900/50 p-4 text-center">
            <p className="text-sm text-neutral-400 mb-1">Avg Cost</p>
            <p className="text-2xl font-bold text-white">
              {overview.statistics.avgCostBps.toFixed(0)} bps
            </p>
          </div>
          <div className="rounded-xl bg-neutral-900/50 p-4 text-center">
            <p className="text-sm text-neutral-400 mb-1">Success Rate</p>
            <p className="text-2xl font-bold text-emerald-400">
              {formatPct(overview.statistics.successRate, 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button
            onClick={onNavigateToPreview}
            className="flex flex-col items-center gap-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30 p-4 hover:bg-cyan-500/20 transition-colors"
            type="button"
          >
            <svg
              className="w-6 h-6 text-cyan-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span className="text-sm font-medium text-cyan-100">Preview Next Rotation</span>
            <span className="text-xs text-neutral-400">See what will happen</span>
          </button>

          <button
            onClick={onNavigateToHistory}
            className="flex flex-col items-center gap-2 rounded-xl bg-purple-500/10 border border-purple-500/30 p-4 hover:bg-purple-500/20 transition-colors"
            type="button"
          >
            <svg
              className="w-6 h-6 text-purple-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium text-purple-100">View History</span>
            <span className="text-xs text-neutral-400">Past rotations timeline</span>
          </button>

          <button
            onClick={onNavigateToSettings}
            className="flex flex-col items-center gap-2 rounded-xl bg-neutral-700/50 border border-white/10 p-4 hover:bg-neutral-700 transition-colors"
            type="button"
          >
            <svg
              className="w-6 h-6 text-neutral-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span className="text-sm font-medium text-neutral-200">Configure</span>
            <span className="text-xs text-neutral-400">Adjust settings</span>
          </button>
        </div>
      </div>

      {/* Current Limits Info */}
      <div className="rounded-2xl border border-white/10 bg-neutral-800/60 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Current Limits</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-neutral-400">Max Turnover</p>
            <p className="text-white font-medium">
              {formatPct(overview.limits.maxTurnoverPct)}
            </p>
          </div>
          <div>
            <p className="text-neutral-400">Max Cost</p>
            <p className="text-white font-medium">{overview.limits.maxCostBps} bps</p>
          </div>
          <div>
            <p className="text-neutral-400">Min Cash Buffer</p>
            <p className="text-white font-medium">
              {formatPct(overview.limits.minCashBufferPct)}
            </p>
          </div>
          <div>
            <p className="text-neutral-400">Max Exposure</p>
            <p className="text-white font-medium">
              {formatPct(overview.limits.maxGrossExposurePct)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
